import { z } from 'zod';
/**
 * The Conversations module's value lists (`patterns/db-schema-conventions.md`,
 * 5). The PostgreSQL enums are built from these same arrays, so the database,
 * the API and the dashboard can't disagree on a value. New values are added by
 * migration; a value in use is never removed or renamed.
 */
/**
 * Who sent a message. The widget's greeting is stored as its own role; `agent`
 * is a person on the website's team replying in a chat they took over (live
 * chat).
 */
export const MESSAGE_ROLES = ['greeting', 'visitor', 'assistant', 'agent'];
export const messageRoleSchema = z.enum(MESSAGE_ROLES);
/** How an answer ended. Set on assistant messages only. */
export const ANSWER_OUTCOMES = ['answered', 'handoff'];
export const answerOutcomeSchema = z.enum(ANSWER_OUTCOMES);
/**
 * Why the assistant handed off. Set only when the outcome is `handoff`.
 * `no_answer` is the knowledge having no answer, the only reason that makes an
 * uncovered question (`product/business-rules.md`, Analytics);
 * `system_error` and `output_rejected` are failed answers
 * (`patterns/error-handling.md`, 4). The assistant feature may add values:
 * `off_topic` is a question that isn't about the business at all, and
 * `off_topic_limit` the contact message a visitor gets, with no model call,
 * once too many of their recent questions were out of scope
 * (`modules/assistant/out-of-scope.ts`). Neither is an uncovered question.
 * `reply_limit` is the contact message once a Free website has used its
 * included AI replies for the cycle, with no model call.
 */
export const HANDOFF_REASONS = [
    'no_answer',
    'guardrail',
    'unsure',
    'visitor_request',
    'emergency',
    'system_error',
    'output_rejected',
    'off_topic',
    'off_topic_limit',
    'reply_limit',
];
export const handoffReasonSchema = z.enum(HANDOFF_REASONS);
/**
 * The model calls one answer can make, each recorded with its tokens and cost
 * for Groundly's admins only (never shown to Account Users): the main answer
 * call, and the second call after a `search_content` tool request.
 */
export const MODEL_CALL_KINDS = ['main', 'tool_round'];
export const modelCallKindSchema = z.enum(MODEL_CALL_KINDS);
/**
 * What happened to a chat's handling, shown in its transcript: an agent took
 * it over from the assistant, handed it to another agent, or ended it.
 */
export const CONVERSATION_EVENT_KINDS = ['taken_over', 'transferred', 'ended'];
export const conversationEventKindSchema = z.enum(CONVERSATION_EVENT_KINDS);
/** The longest reply an agent can send, the same as a visitor message. */
export const MAX_AGENT_MESSAGE_LENGTH = 2000;
/**
 * What an Account User marks an answer as (`product/business-rules.md`,
 * Marks and feedback): `invalid`, it shouldn't have answered at all, or
 * `incorrect`, what it said is wrong. Feedback is required; Groundly reviews
 * every mark to improve answers and guardrails.
 */
export const MARK_KINDS = ['invalid', 'incorrect'];
export const markKindSchema = z.enum(MARK_KINDS);
export const MAX_MARK_FEEDBACK_LENGTH = 1000;
export const markBodySchema = z.object({
    kind: markKindSchema,
    feedback: z
        .string()
        .trim()
        .min(1, 'Say what was wrong with the answer.')
        .max(MAX_MARK_FEEDBACK_LENGTH, `Keep it to ${MAX_MARK_FEEDBACK_LENGTH} characters.`),
});
/**
 * The name a visitor sees on an agent's reply: the first word of the agent's
 * name, so the widget never shows anyone's full name. `null` when they gave
 * none.
 */
export function agentFirstName(name) {
    const first = name?.trim().split(/\s+/)[0];
    return first === undefined || first === '' ? null : first;
}
/**
 * How the visitor became a lead (`product/business-rules.md`, Leads): a
 * contact route they tapped in the chat, or `visitor_details` when they left
 * their own details in the chat's form for the business to get back to them.
 */
export const LEAD_CONTACT_TYPES = [
    'phone_number',
    'contact_form',
    'email',
    'whatsapp',
    'social',
    'visitor_details',
];
export const leadContactTypeSchema = z.enum(LEAD_CONTACT_TYPES);
/** The longest visitor name, and message, the lead form takes. */
export const MAX_LEAD_NAME_LENGTH = 100;
export const MAX_LEAD_MESSAGE_LENGTH = 1000;
/**
 * What a visitor leaves in the chat's lead form: their name and at least one
 * way to reach them. Only what they typed there is kept; the assistant never
 * reads it.
 */
export const leadDetailsSchema = z
    .object({
    name: z.string().trim().min(1, 'Enter your name.').max(MAX_LEAD_NAME_LENGTH),
    email: z
        .string()
        .trim()
        .max(254)
        .transform((v) => (v === '' ? null : v))
        .nullable()
        .pipe(z.email('Enter a valid email address.').nullable()),
    phone: z
        .string()
        .trim()
        .max(40)
        .transform((v) => (v === '' ? null : v))
        .nullable()
        .refine((v) => v === null || /^\+?[\d\s().-]{7,}$/.test(v), 'Enter a valid phone number.'),
    message: z
        .string()
        .trim()
        .max(MAX_LEAD_MESSAGE_LENGTH)
        .transform((v) => (v === '' ? null : v))
        .nullable(),
})
    .refine((d) => d.email !== null || d.phone !== null, {
    message: 'Enter an email address or a phone number.',
    path: ['email'],
});
/**
 * One page an answer was based on, as it was when the answer was given, so a
 * transcript shows what applied then (`product/business-rules.md`, G8 and
 * Industry and guardrail sets).
 *
 * `pagePath` is a path on the website itself: one leading `/`, never `//`,
 * which a browser reads as a link to another host. The dashboard builds the
 * source link from it.
 */
export const messageSourceSchema = z.object({
    pageTitle: z.string(),
    pagePath: z.string().regex(/^\/(?!\/)/, 'A page path starts with a single /.'),
    /**
     * The page's full address as it was when the answer was given, for the
     * widget's source link. Optional: answers stored before it was added have
     * none.
     */
    pageUrl: z.string().optional(),
    /** The sentence the answer relies on, as the page has it: the link scrolls to it. */
    quote: z.string().nullable().optional(),
    /** The heading that sentence sits under on the page. */
    section: z.string().nullable().optional(),
});
//# sourceMappingURL=conversations.js.map